/**
 * Color Palette System for Canvas Grouping
 *
 * Provides distinct, accessible colors for document groups in the Live bucket.
 * Uses deterministic hash-based assignment to ensure the same document name
 * always receives the same color across sessions.
 *
 * Created: October 20, 2025
 * Feature: Canvas Grouping (Live Bucket)
 */

// 16 vibrant, highly distinct colors - curated for maximum differentiation
// Similar colors removed to ensure each is clearly distinguishable
// Updated: October 23, 2025 - Curated vibrant palette (removed duplicate shades)
const PALETTE = [
  // Reds & Oranges (3 distinct)
  '#FF1744', // Ultra Bright Red
  '#FF6E40', // Coral Orange
  '#FFB300', // Golden Orange

  // Yellows & Lime (2 distinct)
  '#FFD600', // Electric Yellow
  '#C6FF00', // Lime Yellow

  // Greens (2 distinct)
  '#00E676', // Neon Green
  '#00C853', // Vivid Green

  // Teals & Cyans (2 distinct)
  '#00E5FF', // Electric Cyan
  '#26A69A', // Teal Green

  // Blues (3 distinct)
  '#2979FF', // Electric Blue
  '#00B0FF', // Sky Blue
  '#448AFF', // Bright Blue

  // Purples & Magentas (4 distinct - widely spaced)
  '#651FFF', // Vivid Purple
  '#E040FB', // Neon Magenta
  '#F50057', // Bright Pink
  '#FF4081', // Hot Pink
];

/**
 * Simple hash function for deterministic color assignment
 * Uses string character codes to generate consistent hash values
 *
 * @param {string} str - String to hash
 * @returns {number} Hash value
 */
const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
};

/**
 * Get consistent color for a document name
 *
 * Same document name will always return the same color, ensuring
 * visual consistency across sessions and page refreshes.
 *
 * @param {string} documentName - The document name to get color for
 * @returns {string} Hex color code
 */
export const getGroupColor = (documentName) => {
  if (!documentName || documentName === '') {
    // Default color for empty/undefined names
    return PALETTE[0];
  }

  const hash = hashString(documentName);
  const index = hash % PALETTE.length;
  return PALETTE[index];
};

/**
 * Get all assigned group colors for a list of document names
 * Returns a map of documentName -> color
 *
 * @param {Array<string>} documentNames - Array of document names
 * @returns {Object} Map of documentName -> color hex code
 */
export const getGroupColors = (documentNames) => {
  const colorMap = {};
  documentNames.forEach(name => {
    colorMap[name] = getGroupColor(name);
  });
  return colorMap;
};

/**
 * Get color with opacity for backgrounds
 * Useful for chips, tags, and highlight backgrounds
 *
 * @param {string} hexColor - Hex color code (e.g., '#5865F2')
 * @param {number} opacity - Opacity value 0-1 (e.g., 0.2 for 20%)
 * @returns {string} RGBA color string
 */
export const getColorWithOpacity = (hexColor, opacity = 0.2) => {
  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Check if a color is light or dark (for text contrast)
 * Returns true if the color is considered "light" (needs dark text)
 *
 * @param {string} hexColor - Hex color code
 * @returns {boolean} True if light color, false if dark
 */
export const isLightColor = (hexColor) => {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate perceived brightness (YIQ formula)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 155;
};

// Export palette for reference
export default PALETTE;
